import { useState } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { MobileBottomNav } from "@/components/mobile";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Wallet,
  ArrowLeftRight,
  TrendingUp,
  History,
  Bell,
  Settings,
  LogOut,
  ChevronDown,
  Zap,
  Shield,
  HelpCircle,
  Lock,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  UserCircle,
  Handshake,
  Home,
} from "lucide-react";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { logOut } from "@/store/actions/authActions";
import { useMyNotifications, useMyUnreadCount, useProfile } from "@/hooks";
import ScrollToTop from "../ScrollToTop";
import SmoothScroll from "../SmoothScroll";
import { KycStatus } from "@/interfaces/kyc";
import type { KycStatus as KycStatusType } from "@/interfaces/kyc";
import type { Notification } from "@/interfaces/notification";
import { format } from "timeago.js";
import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { queryClient } from "@/services/queryClient";
import { Logo } from "../Logo";

// Navigation items that stay within the UserDashboard wrapper
const wrapperNavItems = [
  {
    title: "Home",
    path: "/dashboard",
    icon: Home,
    badge: null,
  },
  { title: "My Wallet", path: "/dashboard/wallet", icon: Wallet, badge: null },
  {
    title: "Transactions",
    path: "/dashboard/transactions",
    icon: History,
    badge: null,
  },
  {
    title: "P2P Merchant",
    path: "/dashboard/p2p",
    icon: Handshake,
    badge: null,
  },
  {
    title: "Trades",
    path: "/dashboard/trades",
    icon: ArrowLeftRight,
    badge: null,
  },
  // {
  //   title: "Rewards & Referrals",
  //   path: "/dashboard/rewards",
  //   icon: Gift,
  //   badge: null,
  // },
  {
    title: "Notifications",
    path: "/dashboard/alerts",
    icon: Bell,
    badge: null,
  },
  {
    title: "KYC Verification",
    path: "/dashboard/kyc",
    icon: Shield,
    badge: null,
  },
  {
    title: "Settings",
    path: "/dashboard/settings",
    icon: Settings,
    badge: null,
  },
];

// Navigation items that go to external pages (outside the wrapper)
const externalNavItems = [
  // { title: "Spot Trading", path: "/spot", icon: TrendingUp, badge: null },
  { title: "P2P Trading", path: "/p2p", icon: ArrowLeftRight, badge: null },
  // { title: "Forex Market", path: "/forex", icon: Zap, badge: "New" },
  { title: "Support", path: "/help", icon: HelpCircle, badge: null },
];

export default function UserDashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const userId = user?.id || "";

  const [isCollapsed, setIsCollapsed] = useState(false);

  const { data: profile, isLoading, isError } = useProfile(userId);

  const approvedKyc = profile?.kycSubmissions.find(
    (kyc) => kyc.status === KycStatus.APPROVED,
  );

  const { data: notificationData } = useMyNotifications(userId);
  const { data: unread } = useMyUnreadCount(userId);

  const unreadNotifications = notificationData?.data?.filter(
    (notif: Notification) => notif?.recipients?.some((r) => !r.isRead),
  );

  const handleNotificationClick = async (id: string) => {
    try {
      const res = await makeRequest.put(`/notification/${id}/read`);

      if (res) {
        queryClient.invalidateQueries({
          queryKey: ["notifications:me", userId],
        });

        queryClient.invalidateQueries({ queryKey: ["unread-count", userId] });

        navigate("/dashboard/alerts");
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
      console.log(error);
    }
  };

  const handleLogout = () => {
    dispatch(logOut());
    toast.success("You have been successfully logged out.");
    navigate("/login");
  };

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    return location.pathname.startsWith(path);
  };

  // Check if we're on a sub-page (not the main dashboard)
  const isSubPage = location.pathname !== "/dashboard";

  // Get page title based on current route
  // const getPageTitle = () => {
  //   const path = location.pathname;
  //   if (path === "/dashboard") return "Dashboard";
  //   if (path === "/analytics") return "Analytics";
  //   if (path.includes("/wallet")) return "My Wallet";
  //   if (path.includes("/history")) return "History";
  //   if (path.includes("/p2p")) return "P2P Management";
  //   if (path.includes("/support")) return "UserSupport";
  //   // if (path.includes("/rewards")) return "Rewards";
  //   if (path.includes("/alerts")) return "Alerts";
  //   if (path.includes("/kyc")) return "KYC";
  //   if (path.includes("/settings")) return "Settings";
  //   return "Dashboard";
  // };

  const getPageTitle = () => {
    const allItems = [...wrapperNavItems, ...externalNavItems];

    const current = allItems.find((item) => {
      if (item.path === "/dashboard") {
        return location.pathname === "/dashboard";
      }

      return location.pathname.startsWith(item.path);
    });

    return current?.title ?? "Dashboard";
  };

  const portfolioChange = 8.32;
  const isPositive = portfolioChange >= 0;

  // const kycStatus: KycStatus = user?.kycStatus ?? KycStatus.PENDING;
  const kycStatus: KycStatusType = approvedKyc?.status ?? KycStatus.PENDING;

  const kycConfig: Record<
    KycStatus,
    { icon: any; color: string; bg: string; label: string }
  > = {
    APPROVED: {
      icon: CheckCircle2,
      color: "text-emerald-500",
      bg: "bg-emerald-500/20",
      label: "Verified",
    },
    PENDING: {
      icon: AlertCircle,
      color: "text-amber-500",
      bg: "bg-amber-500/20",
      label: "Pending",
    },
    REJECTED: {
      icon: AlertCircle,
      color: "text-red-500",
      bg: "bg-red-500/20",
      label: "Rejected",
    },
    DRAFT: {
      icon: Shield,
      color: "text-muted-foreground",
      bg: "bg-muted",
      label: "Not Started",
    },
    UNDER_REVIEW: {
      icon: AlertCircle,
      color: "text-blue-500",
      bg: "bg-blue-500/20",
      label: "Under Review",
    },
    NEEDS_MORE_INFO: {
      icon: AlertCircle,
      color: "text-blue-500",
      bg: "bg-blue-500/20",
      label: "Under Review",
    },
  };

  const kyc = kycConfig[kycStatus];

  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex min-h-screen w-full bg-background">
        <ScrollToTop />
        <SmoothScroll />
        {/* Desktop Sidebar - Hidden on mobile */}
        <aside
          className={cn(
            "fixed left-0 top-0 h-screen z-40 bg-sidebar border-r border-sidebar-border flex-col transition-all duration-300 ease-in-out hidden lg:flex",
            isCollapsed ? "w-[70px]" : "w-[260px]",
          )}
        >
          {/* Sidebar Header */}
          <div className="h-16 border-b border-sidebar-border flex items-center px-3 shrink-0">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate("/dashboard")}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl shrink-0">
                <Logo
                  iconOnly
                  size="xl"
                  className="transition-transform duration-300"
                />
              </div>
              {!isCollapsed && (
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-sidebar-foreground">
                    My Dashboard
                  </span>
                  <span className="text-xs text-sidebar-foreground/60">
                    Welcome back!
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar Content - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden py-4">
            {/* Wrapper Navigation */}
            <nav className="px-2 mb-4">
              <p
                className={cn(
                  "text-xs text-sidebar-foreground/50 mb-2 px-2",
                  isCollapsed && "hidden",
                )}
              >
                Dashboard
              </p>
              <ul className="space-y-1">
                {wrapperNavItems.map((item) => {
                  const active = isActive(item.path);

                  return (
                    <li key={item.path}>
                      {isCollapsed ? (
                        <Tooltip delayDuration={100}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => navigate(item.path)}
                              className={cn(
                                "w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-all duration-200 relative",
                                active
                                  ? "bg-primary/20 text-primary"
                                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                              )}
                            >
                              <item.icon
                                className={cn(
                                  "h-5 w-5",
                                  active ? "text-primary" : "",
                                )}
                              />
                              {active && (
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" sideOffset={10}>
                            <div className="flex items-center gap-2">
                              {item.title}
                              {item.badge && (
                                <Badge className="h-4 min-w-4 px-1 text-[10px]">
                                  {item.badge}
                                </Badge>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <button
                          onClick={() => navigate(item.path)}
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 relative",
                            active
                              ? "bg-primary/20 text-primary"
                              : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                          )}
                        >
                          <item.icon
                            className={cn(
                              "h-5 w-5 shrink-0",
                              active ? "text-primary" : "",
                            )}
                          />
                          <span className="font-medium text-sm flex-1 text-left">
                            {item.title}
                          </span>
                          {item.badge && (
                            <Badge className="h-5 min-w-5 px-1.5 text-[10px] bg-primary/20 text-primary border-0">
                              {item.badge}
                            </Badge>
                          )}
                          {active && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full" />
                          )}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* External Navigation */}
            <nav className="px-2">
              <p
                className={cn(
                  "text-xs text-sidebar-foreground/50 mb-2 px-2",
                  isCollapsed && "hidden",
                )}
              >
                Trading & More
              </p>
              <ul className="space-y-1">
                {externalNavItems.map((item) => {
                  const active = isActive(item.path);

                  return (
                    <li key={item.path}>
                      {isCollapsed ? (
                        <Tooltip delayDuration={100}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => navigate(item.path)}
                              className={cn(
                                "w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-all duration-200 relative",
                                active
                                  ? "bg-primary/10 text-primary"
                                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                              )}
                            >
                              <item.icon
                                className={cn(
                                  "h-5 w-5",
                                  active ? "text-primary" : "",
                                )}
                              />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="right" sideOffset={10}>
                            <div className="flex items-center gap-2">
                              {item.title}
                              {item.badge && (
                                <Badge className="h-4 min-w-4 px-1 text-[10px]">
                                  {item.badge}
                                </Badge>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <button
                          onClick={() => navigate(item.path)}
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 relative",
                            active
                              ? "bg-primary/10 text-primary"
                              : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground",
                          )}
                        >
                          <item.icon
                            className={cn(
                              "h-5 w-5 shrink-0",
                              active ? "text-primary" : "",
                            )}
                          />
                          <span className="font-medium text-sm flex-1 text-left">
                            {item.title}
                          </span>
                          {item.badge && (
                            <Badge className="h-5 min-w-5 px-1.5 text-[10px] bg-emerald-500/20 text-emerald-500 border-0">
                              {item.badge}
                            </Badge>
                          )}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Sidebar Footer */}
          <div className="border-t border-sidebar-border p-3 shrink-0">
            {/* KYC Status */}
            {!isCollapsed && (
              <div
                className="rounded-lg bg-sidebar-accent/50 p-3 mb-3 cursor-pointer hover:bg-sidebar-accent transition-colors"
                onClick={() => navigate("/dashboard/kyc")}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      "h-8 w-8 rounded-lg flex items-center justify-center",
                      kyc.bg,
                    )}
                  >
                    <kyc.icon className={cn("h-4 w-4", kyc.color)} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium">KYC Status</p>
                    <p className={cn("text-xs", kyc.color)}>{kyc.label}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-sidebar-foreground/40" />
                </div>
              </div>
            )}

            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-sidebar-accent transition-colors">
                  <Avatar className="h-8 w-8 border-2 border-primary/30 shrink-0">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-primary/20 text-primary text-xs">
                      {user?.firstName?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  {!isCollapsed && (
                    <>
                      <div className="flex flex-col items-start flex-1 min-w-0">
                        <span className="text-sm font-medium truncate">
                          {user?.username || "User Name"}
                        </span>
                        <span className="text-xs text-sidebar-foreground/60 truncate">
                          {user?.email || "user@example.com"}
                        </span>
                      </div>
                      <ChevronDown className="h-4 w-4 text-sidebar-foreground/50 shrink-0" />
                    </>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate("/dashboard/profile")}
                >
                  <UserCircle className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/dashboard/settings")}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate("/dashboard/settings?tab=security")}
                >
                  <Lock className="mr-2 h-4 w-4" />
                  Security
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-500"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Main Content Area */}
        <div
          className={cn(
            "flex-1 flex flex-col min-w-0 transition-all duration-300 ease-in-out",
            "lg:ml-0",
            isCollapsed ? "lg:ml-[70px]" : "lg:ml-[260px]",
          )}
        >
          {/* Mobile Header - Clean with back button */}
          <header className="lg:hidden fixed top-0 left-0 right-0 z-30 h-20 bg-background/95 backdrop-blur flex items-center justify-between p-3">
            {/* Left: Back button on sub-pages */}
            <div className="flex items-center gap-3">
              {isSubPage ? (
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center justify-center w-9 h-9 rounded-full hover:bg-muted transition-colors"
                >
                  <ArrowLeft className="h-8 w-8 font-bold" />
                </button>
              ) : (
                <div className="ml-2 font-semibold text-xl">
                  {getPageTitle()}
                </div>
              )}
            </div>
            {/* <User/> */}
            {isSubPage && (
              <div className="ml-2 font-semibold text-xl">{getPageTitle()}</div>
            )}

            {/* Right: Notifications only */}
            <button
              onClick={() => navigate("/dashboard/alerts")}
              className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-muted transition-colors"
            >
              <Bell className="h-6 w-6" />
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />
            </button>
          </header>

          {/* Desktop Header */}
          <header
            className={cn(
              "hidden lg:flex fixed top-0 left-0 right-0 z-30 h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 items-center justify-between gap-4 px-6 transition-all duration-300 ease-in-out",
              isCollapsed ? "lg:left-[70px]" : "lg:left-[260px]",
            )}
          >
            {/* Left Section */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <button
                className="flex p-2 hover:bg-muted rounded-lg shrink-0"
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-5 w-5" />
                ) : (
                  <ChevronLeft className="h-5 w-5" />
                )}
              </button>

              <div className="flex flex-col min-w-0">
                <h1 className="text-lg font-semibold truncate">Dashboard</h1>
                <p className="text-xs text-muted-foreground hidden md:block">
                  Overview of your portfolio
                </p>
              </div>
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2 shrink-0">
              {/* <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/dashboard/wallet")}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Deposit
              </Button> */}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    {unread > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-medium text-primary-foreground flex items-center justify-center">
                        {unread}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    Notifications
                    <Badge variant="secondary" className="text-[10px]">
                      {unread} new
                    </Badge>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {unreadNotifications
                    ?.slice(0, 5)
                    .map((notif: Notification) => {
                      return (
                        <DropdownMenuItem
                          key={notif.id}
                          className="flex items-start gap-3 p-3 cursor-pointer"
                          onClick={() => handleNotificationClick(notif.id)}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{notif.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {notif.description}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(notif.createdAt)}
                            </p>
                          </div>
                        </DropdownMenuItem>
                      );
                    })}

                  <DropdownMenuSeparator />

                  <div className="p-2">
                    <DropdownMenuItem
                      onClick={() => navigate("/dashboard/alerts")}
                      className="justify-center font-medium cursor-pointer"
                    >
                      View All Notifications
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              {/* 
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/help")}
              >
                <HelpCircle className="h-5 w-5" />
              </Button> */}
            </div>
          </header>

          {/* Page Content - with padding for headers */}
          <main className="flex-1 p-1 lg:p-6 overflow-auto pt-16 lg:pt-20 pb-24 lg:pb-6">
            <div className="mx-auto max-w-7xl">
              <Outlet />
            </div>
          </main>
        </div>

        {/* Mobile Bottom Navigation */}
        <div className="lg:hidden">
          <MobileBottomNav userType="user" />
        </div>
      </div>
    </TooltipProvider>
  );
}
